<template>
  <div class="container-fluid py-4">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <!-- 2행 레이아웃으로 변경 -->
            <div class="d-flex flex-column">
              <!-- 첫 번째 행: 경로 네비게이션 -->
              <div class="d-flex justify-content-between align-items-center mb-3">
                <div class="d-flex align-items-center gap-2">
                  <!-- 경로 네비게이션 -->
                  <nav aria-label="breadcrumb" class="d-flex align-items-center">
                    <ol class="breadcrumb mb-0">
                      <li 
                        v-for="(crumb, index) in breadcrumbs" 
                        :key="index"
                        class="breadcrumb-item"
                        :class="{ 'active': index === breadcrumbs.length - 1 }"
                      >
                        <a 
                          v-if="index !== breadcrumbs.length - 1" 
                          href="#" 
                          @click.prevent="navigateToPath(crumb.path)"
                        >
                          {{ crumb.name }}
                        </a>
                        <span v-else>{{ crumb.name }}</span>
                      </li>
                    </ol>
                  </nav>
                  <span class="text-muted mx-2">|</span>
                  <div class="table-info">
                    전체 <span class="text-primary">{{ files.length }}</span>개의 파일
                  </div>
                </div>
              </div>
              
              <!-- 두 번째 행: 기능 버튼들 -->
              <div class="d-flex justify-content-between align-items-center">
                <!-- 그룹 1: 검색 입력 필드 (추가) -->
                <div class="control-group me-4">
                  <div class="control-label mb-1">검색</div>
                  <div class="d-flex gap-2 align-items-center">
                    <a-input
                      v-model:value="searchKeyword"
                      placeholder="폴더 또는 파일 검색"
                      style="width: 250px;"
                      @press-enter="onSearch(searchKeyword)"
                      size="small"
                      allow-clear
                    />
                    <a-radio-group v-model:value="searchType" size="small" v-if="isSearchMode">
                      <a-radio-button value="all">전체</a-radio-button>
                      <a-radio-button value="folder">폴더만</a-radio-button>
                      <a-radio-button value="file">파일만</a-radio-button>
                    </a-radio-group>
                  </div>
                </div>
                
                <!-- 그룹 2: 컨테이너 선택 -->
                <div class="control-group me-4">
                  <div class="control-label mb-1">저장소</div>
                  <a-select 
                    v-model:value="currentContainer" 
                    class="container-select"
                    @change="onContainerChange"
                  >
                    <a-select-option 
                      v-for="container in containers" 
                      :key="container" 
                      :value="container"
                    >
                      {{ container }}
                    </a-select-option>
                  </a-select>
                </div>
                
                <!-- 그룹 3: 액션 버튼 (파일 업로드, 폴더 생성) -->
                <div class="control-group me-4">
                  <div class="control-label mb-1">작업</div>
                  <div class="d-flex gap-2">
                    <a-button type="primary" @click="goToUploadPage">
                      <template #icon><upload-outlined /></template>
                      파일 업로드
                    </a-button>
                    <a-button type="primary" @click="showCreateFolderModal = true">
                      <template #icon><folder-add-outlined /></template>
                      폴더 생성
                    </a-button>
                    <a-button type="primary" @click="refreshFiles">
                      <template #icon><reload-outlined :spin="loading" /></template>
                      새로고침
                    </a-button>
                  </div>
                </div>
                
                <!-- 그룹 4: 보기 옵션 (목록/그리드 전환, 페이지 크기) -->
                <div class="control-group">
                  <div class="control-label mb-1">보기 옵션</div>
                  <div class="d-flex gap-2 align-items-center">
                    <a-radio-group v-model:value="viewMode" button-style="solid" size="middle" class="view-mode-buttons">
                      <a-radio-button value="list">
                        <template #icon><UnorderedListOutlined /></template>
                        목록
                      </a-radio-button>
                      <a-radio-button value="grid">
                        <template #icon><AppstoreOutlined /></template>
                        그리드
                      </a-radio-button>
                    </a-radio-group>
                    
                    <a-select 
                      v-model:value="pageSize" 
                      class="page-size-select"
                      :options="pageSizes"
                      @change="onPageSizeChange"
                      size="middle"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="card-body">
            <!-- 검색 결과 알림 (추가) -->
            <div v-if="isSearchMode" class="search-info mb-3">
              <a-alert type="info" show-icon>
                <template #message>
                  <div class="d-flex justify-content-between align-items-center">
                    <span>"<strong>{{ searchKeyword }}</strong>" 검색 결과 ({{ files.length }}개 항목)</span>
                    <a-button type="link" @click="clearSearch" class="p-0">
                      검색 해제
                    </a-button>
                  </div>
                </template>
              </a-alert>
            </div>
            
            <!-- 리스트 뷰 -->
            <a-table
              v-if="viewMode === 'list'"
              :columns="columns"
              :data-source="files"
              :loading="loading"
              :pagination="false"
            >
              <!-- 파일/폴더 이름 -->
              <template #name="{ text, record }">
                <div class="d-flex align-items-center">
                  <i :class="getItemIcon(record)" class="me-2"></i>
                  <a 
                    v-if="record.isFolder" 
                    href="#" 
                    @click.prevent="navigateToFolder(record.path)"
                    class="folder-link"
                  >
                    {{ getDisplayName(record.name) }}
                  </a>
                  <a-tooltip v-else :title="record.name">
                    <span class="ellipsis-text">{{ getDisplayName(record.name) }}</span>
                  </a-tooltip>
                </div>
              </template>

              <!-- 파일 크기 -->
              <template #size="{ text, record }">
                <span v-if="!record.isFolder">{{ formatFileSize(text) }}</span>
                <span v-else>-</span>
              </template>

              <!-- 파일 타입 -->
              <template #contentType="{ text, record }">
                <span v-if="!record.isFolder">
                  <a-tag :color="getContentTypeColor(text)">
                    {{ getContentTypeLabel(text) }}
                  </a-tag>
                </span>
                <span v-else>
                  <a-tag color="blue">폴더</a-tag>
                </span>
              </template>

              <!-- 생성일 -->
              <template #createdOn="{ text }">
                {{ formatDateWithTime(text) }}
              </template>

              <!-- 수정일 -->
              <template #lastModified="{ text }">
                {{ formatDateWithTime(text) }}
              </template>

              <!-- 작업 -->
              <template #action="{ record }">
                <div class="action-buttons">
                  <template v-if="!record.isFolder">
                    <a-button type="link" @click="handlePreview(record)">
                      <template #icon><eye-outlined /></template>
                    </a-button>
                    <a-button type="link" @click="downloadFile(record)">
                      <template #icon><download-outlined /></template>
                    </a-button>
                    <a-button type="link" @click="showRenameModal = true; selectedFile = record">
                      <template #icon><edit-outlined /></template>
                    </a-button>
                  </template>
                  <template v-else>
                    <a-button type="link" @click="showRenameModal = true; selectedFile = record">
                      <template #icon><edit-outlined /></template>
                    </a-button>
                  </template>
                </div>
              </template>
            </a-table>

            <!-- 그리드 뷰 -->
            <div v-else class="grid-view">
              <a-row :gutter="[16, 16]">
                <a-col 
                  v-for="item in files" 
                  :key="item.path"
                  :xs="12"
                  :sm="8"
                  :md="6"
                  :lg="4"
                  :xl="3"
                >
                  <a-card 
                    :class="['item-card', { 'is-folder': item.isFolder }]"
                    @click="item.isFolder ? navigateToFolder(item.path) : null"
                  >
                    <div class="item-icon">
                      <i :class="getItemIcon(item)"></i>
                    </div>
                    <div class="item-info">
                      <a-tooltip :title="item.name">
                        <div class="item-name">{{ getDisplayName(item.name) }}</div>
                      </a-tooltip>
                      <div class="item-meta">
                        <span v-if="!item.isFolder">{{ formatFileSize(item.size) }}</span>
                        <span v-else>폴더</span>
                      </div>
                    </div>
                    <div class="item-actions">
                      <template v-if="!item.isFolder">
                        <a-button type="link" @click.stop="handlePreview(item)">
                          <template #icon><eye-outlined /></template>
                        </a-button>
                        <a-button type="link" @click.stop="downloadFile(item)">
                          <template #icon><download-outlined /></template>
                        </a-button>
                        <a-button type="link" @click.stop="showRenameModal = true; selectedFile = item">
                          <template #icon><edit-outlined /></template>
                        </a-button>
                      </template>
                      <template v-else>
                        <a-button type="link" @click.stop="showRenameModal = true; selectedFile = item">
                          <template #icon><edit-outlined /></template>
                        </a-button>
                      </template>
                    </div>
                  </a-card>
                </a-col>
              </a-row>
            </div>

            <!-- 페이지네이션 -->
            <div class="pagination-wrapper">
              <a-pagination
                v-if="continuationToken"
                :current="currentPage"
                :total="totalCount"
                :pageSize="pageSize"
                @change="onPageChange"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 모달 컴포넌트들 -->
    <CreateFolderModal
      v-model:visible="showCreateFolderModal"
      @create="handleCreateFolder"
      :current-path="currentPath"
    />

    <RenameFileModal
      v-model:visible="showRenameModal"
      :current-file-name="selectedFile?.name || ''"
      :is-folder="selectedFile?.isFolder || false"
      @rename="handleRename"
      @update:visible="onRenameModalClose"
    />

    <PreviewModal
      v-model:visible="showPreviewModal"
      :file="selectedFileForPreview"
    />
  </div>
</template>

<script>
import { 
  UploadOutlined, 
  FolderAddOutlined, 
  DownloadOutlined,
  EditOutlined,
  UnorderedListOutlined,
  AppstoreOutlined,
  EyeOutlined,
  SearchOutlined,
  ReloadOutlined
} from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';
import storageApis from './api';
import CreateFolderModal from './components/CreateFolderModal.vue';
import RenameFileModal from './components/RenameFileModal.vue';
import PreviewModal from './components/PreviewModal.vue';
import { formatFileSize } from '@/utils/fileSize';

export default {
  name: 'StorageManagement',
  components: {
    UploadOutlined,
    FolderAddOutlined,
    DownloadOutlined,
    EditOutlined,
    EyeOutlined,
    UnorderedListOutlined,
    AppstoreOutlined,
    SearchOutlined,
    ReloadOutlined,
    CreateFolderModal,
    RenameFileModal,
    PreviewModal
  },
  data() {
    return {
      loading: false,
      files: [],
      currentPage: 1,
      pageSize: 20,
      totalCount: 0,
      continuationToken: null,
      showCreateFolderModal: false,
      showRenameModal: false,
      selectedFile: null,
      storageApi: null,
      currentContainer: 'certilife',
      containers: ['certilife'],
      viewMode: 'list',
      currentPath: '/',
      breadcrumbs: [{ name: 'Root', path: '/' }],
      searchKeyword: '',
      searchType: 'all',
      searchLoading: false,
      isSearchMode: false,
      columns: [
        {
          title: '이름',
          dataIndex: 'name',
          key: 'name',
          slots: { customRender: 'name' },
          width: '30%'
        },
        {
          title: '크기',
          dataIndex: 'size',
          key: 'size',
          slots: { customRender: 'size' },
          width: '120px',
          sorter: (a, b) => a.size - b.size
        },
        {
          title: '유형',
          dataIndex: 'contentType',
          key: 'contentType',
          slots: { customRender: 'contentType' },
          width: '120px'
        },
        {
          title: '생성일',
          dataIndex: 'createdOn',
          key: 'createdOn',
          slots: { customRender: 'createdOn' },
          width: '200px',
          sorter: (a, b) => new Date(a.createdOn) - new Date(b.createdOn)
        },
        {
          title: '수정일',
          dataIndex: 'lastModified',
          key: 'lastModified',
          slots: { customRender: 'lastModified' },
          width: '200px',
          sorter: (a, b) => new Date(a.lastModified) - new Date(b.lastModified)
        },
        {
          title: '작업',
          key: 'action',
          slots: { customRender: 'action' },
          width: '150px',
          fixed: 'right'
        }
      ],
      showPreviewModal: false,
      selectedFileForPreview: null,
    };
  },
  computed: {
    pageSizes() {
      return [
        { value: 10, label: '10개씩 보기' },
        { value: 20, label: '20개씩 보기' },
        { value: 50, label: '50개씩 보기' }
      ];
    }
  },
  created() {
    this.storageApi = storageApis(this.$apiInstance, this.$store);
    this.fetchContainers();
    this.loadFiles();
  },
  methods: {
    // 파일 업로드 페이지로 이동
    goToUploadPage() {
      this.$router.push({
        path: '/storage/upload',
        query: { 
          path: this.currentPath, 
          container: this.currentContainer 
        }
      });
    },
    // 파일 목록 로드
    async loadFiles(folder = '', continuationToken = null) {
      this.loading = true;
      
      try {
        const params = { 
          pageSize: this.pageSize 
        };
        
        if (continuationToken) {
          params.continuationToken = continuationToken;
        }
        
        console.log(`파일 목록 조회 시작: 폴더='${folder}', 컨테이너='${this.currentContainer}', 페이지 크기=${this.pageSize}`);
        
        const response = await this.storageApi.getFiles(
          folder,
          params,
          this.currentContainer
        );
        
        console.log('파일 목록 조회 응답:', response);
        
        if (response.success) {
          const data = response.data;
          
          // 새로운 응답 구조 처리
          let items = (data.items || []).map(item => ({
            key: item.path,
            ...item,
            isFolder: item.type === 'folder'
          }));
          
          // 서버에서 페이지 크기가 제대로 적용되지 않는 경우를 대비해 클라이언트에서 처리
          if (items.length > this.pageSize) {
            console.log(`서버가 요청된 페이지 크기(${this.pageSize})보다 많은 항목(${items.length}개)을 반환했습니다. 클라이언트에서 처리합니다.`);
            items = items.slice(0, this.pageSize);
          }
          
          this.files = items;
          
          // 경로 정보 업데이트
          this.currentPath = data.currentPath || '/';
          this.breadcrumbs = data.breadcrumbs || [{ name: 'Root', path: '/' }];
          this.continuationToken = data.continuationToken;
          
          // 전체 항목 수 업데이트 (페이지네이션을 위해)
          this.totalCount = data.totalCount || items.length;
          
          if (this.files.length === 0) {
            message.info(`'${this.currentPath}'에 항목이 없습니다.`);
          }
        } else {
          console.error('파일 목록 조회 실패:', response.error);
          message.error(response.error || '파일 목록을 불러오는데 실패했습니다.');
          this.files = [];
        }
      } catch (error) {
        console.error('파일 목록 로드 오류:', error);
        message.error('파일 목록을 불러오는데 실패했습니다: ' + (error.message || '알 수 없는 오류'));
        this.files = [];
      } finally {
        this.loading = false;
      }
    },
    onContainerChange(value) {
      if (this.isSearchMode) {
        this.isSearchMode = false;
        this.searchKeyword = '';
      }
      
      this.currentContainer = value;
      this.currentPage = 1;
      this.continuationToken = null;
      this.loadFiles();
    },
    onPageSizeChange(size) {
      console.log(`페이지 크기 변경: ${this.pageSize} → ${size}`);
      this.pageSize = size;
      this.currentPage = 1;
      this.continuationToken = null;
      this.loadFiles(this.currentPath);
    },
    onPageChange(page) {
      this.currentPage = page;
      this.loadFiles();
    },
    handleFileSelect(info) {
      this.selectedFiles = info.fileList;
    },
    
    handleCreateFolder(folderPath) {
      this.storageApi.createFolder(
        folderPath,
        (response) => {
          if (response.data.success) {
            message.success(response.data.message || '폴더가 성공적으로 생성되었습니다.');
            this.showCreateFolderModal = false;
            this.loadFiles();
          } else {
            console.error('폴더 생성 실패:', response.data.error);
            message.error(response.data.error || '폴더 생성에 실패했습니다.');
          }
        },
        (error) => {
          console.error('폴더 생성 실패:', error);
          message.error('폴더 생성에 실패했습니다.');
        }
      );
    },
    handleRename(newName) {
      if (!this.selectedFile) return;
      
      // isFolder 속성으로 폴더와 파일 구분
      if (this.selectedFile.isFolder) {
        // 폴더 이름 변경 API 호출
        this.storageApi.renameFolder(
          this.selectedFile.path,
          newName,
          (response) => {
            if (response.data.success) {
              message.success(response.data.message || '폴더 이름이 성공적으로 변경되었습니다.');
              this.showRenameModal = false;
              this.selectedFile = null;
              this.loadFiles();
            } else {
              console.error('폴더 이름 변경 실패:', response.data.error);
              message.error(response.data.error || '폴더 이름 변경에 실패했습니다.');
            }
          },
          (error) => {
            console.error('폴더 이름 변경 실패:', error);
            message.error(error.response?.data?.error || '폴더 이름 변경에 실패했습니다.');
          }
        );
      } else {
        // 파일 이름 변경 API 호출
        this.storageApi.renameFile(
          this.selectedFile.name,
          newName,
          (response) => {
            if (response.data.success) {
              message.success(response.data.message || '파일 이름이 성공적으로 변경되었습니다.');
              this.showRenameModal = false;
              this.selectedFile = null;
              this.loadFiles();
            } else {
              console.error('파일 이름 변경 실패:', response.data.error);
              message.error(response.data.error || '파일 이름 변경에 실패했습니다.');
            }
          },
          (error) => {
            console.error('파일 이름 변경 실패:', error);
            message.error(error.response?.data?.error || '파일 이름 변경에 실패했습니다.');
          }
        );
      }
    },
    getContentTypeLabel(type) {
      if (!type) return '알 수 없음';
      const parts = type.split('/');
      return parts[1]?.toUpperCase() || parts[0]?.toUpperCase() || type;
    },
    getContentTypeColor(type) {
      if (!type) return 'default';
      const mainType = type.split('/')[0];
      const colors = {
        'image': 'green',
        'application': 'blue',
        'text': 'purple',
        'video': 'orange',
        'audio': 'cyan'
      };
      return colors[mainType] || 'default';
    },
    formatDateWithTime(dateString) {
      if (!dateString) return '-';
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        return date.toLocaleString('ko-KR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hourCycle: 'h23'
        });
      } catch (e) {
        return '-';
      }
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        return date.toLocaleDateString('ko-KR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        });
      } catch (e) {
        return '-';
      }
    },
    getItemIcon(item) {
      if (item.isFolder) {
        return 'fas fa-folder';
      }
      
      if (!item.name) return 'fas fa-file';
      
      const extension = item.name.split('.').pop().toLowerCase();
      switch (extension) {
        case 'pdf':
          return 'fas fa-file-pdf';
        case 'doc':
        case 'docx':
          return 'fas fa-file-word';
        case 'xls':
        case 'xlsx':
          return 'fas fa-file-excel';
        case 'jpg':
        case 'jpeg':
        case 'png':
          return 'fas fa-file-image';
        default:
          return 'fas fa-file';
      }
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    getDisplayName(name) {
      if (!name) return '이름 없음';
      const parts = name.split('/');
      return parts[parts.length - 1];
    },
    downloadFile(file) {
      if (!file || file.isFolder) return;
      
      try {
        this.loading = true;
        const fileName = this.getDisplayName(file.name || file.path);
        
        // 파일 경로 또는 이름 추출
        let blobPath = file.path || file.name;
        if (!blobPath) {
          message.error('파일 경로가 유효하지 않습니다.');
          return;
        }
        
        // URL 인코딩 (슬래시는 보존)
        const encodedPath = encodeURIComponent(blobPath).replace(/%2F/g, '/');
        
        // 컨테이너 정보 추가
        const container = this.currentContainer;
        const containerParam = container ? `?container=${container}` : '';
        
        // 다운로드 URL 구성 (API 또는 직접 스토리지 접근)
        const downloadUrl = `/object-storages/azure/files/${encodedPath}/download${containerParam}`;
        
        // 진행 상태 표시
        const hide = message.loading(`${fileName} 다운로드 중...`, 0);
        
        // 다운로드 방식 1: 백엔드 API를 통한 다운로드
        this.$apiInstance({
          url: downloadUrl,
          method: 'GET',
          responseType: 'blob',  // 중요: blob 형식으로 응답 받기
        }).then(response => {
          // 성공 시 파일 다운로드 처리
          const contentType = response.headers['content-type'];
          const blob = new Blob([response.data], { type: contentType });
          
          // 브라우저에서 다운로드 처리
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();
          
          // 리소스 정리
          window.URL.revokeObjectURL(url);
          document.body.removeChild(link);
          
          message.success(`${fileName} 다운로드 완료`);
        }).catch(error => {
          console.error('파일 다운로드 실패:', error);
          message.error(`다운로드 실패: ${error.message || '알 수 없는 오류'}`);
          
          // 직접 다운로드 방식으로 추가 시도 (필요시)
          // this.directDownloadFile(file);
        }).finally(() => {
          hide();
          this.loading = false;
        });
      } catch (error) {
        console.error('다운로드 처리 중 오류:', error);
        message.error(`다운로드 처리 중 오류: ${error.message || '알 수 없는 오류'}`);
        this.loading = false;
      }
    },
    
    // 대체 다운로드 방식: Azure Blob Storage URL 직접 접근 (필요시 활성화)
    directDownloadFile(file) {
      try {
        // 컨테이너 이름 (기본값: certilife)
        const containerName = this.currentContainer || 'certilife';
        
        // 파일 경로 또는 이름 추출
        let fileName = file.name;
        if (file.path) {
          const pathParts = file.path.split('/');
          fileName = pathParts[pathParts.length - 1];
        }
        
        // Azure Blob Storage 직접 URL 구성
        const directUrl = `https://certilife.blob.core.windows.net/${containerName}/${fileName}`;
        
        // 새 창에서 열기
        window.open(directUrl, '_blank');
      } catch (error) {
        console.error('직접 다운로드 실패:', error);
        message.error(`직접 다운로드 실패: ${error.message || '알 수 없는 오류'}`);
      }
    },
    // 사용 가능한 컨테이너 목록 가져오기
    async fetchContainers() {
      try {
        // API 호출로 컨테이너 목록 가져오기
        const response = await this.$apiInstance.get('/object-storages/azure/status');
        if (response.data.success && response.data.data) {
          const data = response.data.data;
          if (data.containers && Array.isArray(data.containers)) {
            this.containers = data.containers;
            // 기본 컨테이너가 있으면 선택
            if (data.defaultContainer && this.containers.includes(data.defaultContainer)) {
              this.currentContainer = data.defaultContainer;
            }
          }
        }
      } catch (error) {
        console.error('컨테이너 목록 조회 실패:', error);
        message.error('저장소 목록을 불러오는데 실패했습니다.');
      }
    },
    // 폴더 네비게이션
    navigateToFolder(path) {
      if (this.isSearchMode) {
        this.isSearchMode = false;
        this.searchKeyword = '';
      }
      
      this.currentPath = path;
      this.loadFiles(path);
    },
    navigateToPath(path) {
      this.navigateToFolder(path);
    },
    // 파일 미리보기 메서드 추가
    handlePreview(file) {
      if (!file || file.isFolder) return;
      
      this.selectedFileForPreview = {
        ...file,
        contentType: file.contentType || this.getContentTypeFromName(file.name)
      };
      this.showPreviewModal = true;
    },
    getContentTypeFromName(filename) {
      if (!filename) return 'application/octet-stream';
      const ext = filename.split('.').pop().toLowerCase();
      const mimeTypes = {
        'jpg': 'image/jpeg',
        'jpeg': 'image/jpeg',
        'png': 'image/png',
        'gif': 'image/gif',
        'pdf': 'application/pdf',
        'txt': 'text/plain',
        'json': 'application/json',
        'html': 'text/html',
        'css': 'text/css',
        'js': 'text/javascript'
      };
      return mimeTypes[ext] || 'application/octet-stream';
    },
    onRenameModalClose(visible) {
      if (!visible) {
        this.selectedFile = null;
      }
    },
    async onSearch(value) {
      if (!value || value.trim() === '') {
        return;
      }
      
      this.searchLoading = true;
      this.loading = true;
      
      try {
        const params = {
          type: this.searchType,
          pageSize: this.pageSize
        };
        
        console.log(`검색 시작: 키워드="${value}", 유형=${this.searchType}, 페이지 크기=${this.pageSize}`);
        
        const response = await this.storageApi.searchFiles(
          value,
          params,
          this.currentContainer
        );
        
        console.log('검색 응답:', response);
        
        if (response.success) {
          const data = response.data;
          
          // 검색 결과 처리
          let items = (data.items || []).map(item => ({
            key: item.path,
            ...item,
            isFolder: item.type === 'folder'
          }));
          
          // 검색 결과가 없는 경우 메시지 표시
          if (items.length === 0) {
            message.info(`"${value}"에 대한 검색 결과가 없습니다.`);
          }
          
          this.files = items;
          this.isSearchMode = true;
          
          // 검색 모드에서는 breadcrumbs와 현재 경로를 특별히 처리
          this.breadcrumbs = [
            { name: 'Root', path: '/' },
            { name: `"${value}" 검색 결과`, path: null }
          ];
          
          // 기타 페이지네이션 정보 업데이트
          this.continuationToken = data.continuationToken;
          this.totalCount = data.totalCount || items.length;
        } else {
          console.error('검색 실패:', response.error);
          message.error(response.error || '검색에 실패했습니다.');
        }
      } catch (error) {
        console.error('검색 중 오류:', error);
        message.error('검색 중 오류가 발생했습니다: ' + (error.message || '알 수 없는 오류'));
      } finally {
        this.searchLoading = false;
        this.loading = false;
      }
    },
    
    /**
     * 검색 결과 초기화
     */
    clearSearch() {
      this.searchKeyword = '';
      this.isSearchMode = false;
      this.loadFiles(this.currentPath);
    },
    
    /**
     * 검색 타입 변경 시 재검색
     */
    onSearchTypeChange() {
      if (this.isSearchMode && this.searchKeyword) {
        this.onSearch(this.searchKeyword);
      }
    },
    refreshFiles() {
      message.info(`'${this.currentPath}' 경로 새로고침 중...`);
      this.loadFiles(this.currentPath);
    }
  },
  watch: {
    // 검색 타입 변경 감지
    searchType() {
      this.onSearchTypeChange();
    },
    // 이름 변경 모달이 닫힐 때 상태 초기화
    showRenameModal(newVal) {
      if (!newVal) {
        this.selectedFile = null;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
// 컨트롤 그룹 관련 스타일
.control-group {
  .control-label {
    font-size: 0.8rem;
    color: rgba(0, 0, 0, 0.65);
    font-weight: 500;
  }
  
  // 보기 옵션 버튼 크기 조정
  .view-mode-buttons {
    :deep(.ant-radio-button-wrapper) {
      height: 32px;
      line-height: 30px;
      padding: 0 15px;
      
      .anticon {
        font-size: 14px;
      }
    }
  }
  
  // 모든 버튼의 일관된 높이 보장
  :deep(.ant-btn), :deep(.ant-select-selector), :deep(.ant-radio-button-wrapper) {
    height: 32px !important;
  }
  
  // 버튼 내 아이콘 크기 통일
  :deep(.anticon) {
    font-size: 14px;
  }
}

// 반응형 스타일 개선
@media (max-width: 1200px) {
  .card-header {
    .d-flex.justify-content-between.align-items-center {
      flex-wrap: wrap;
      gap: 16px;
      
      .control-group {
        margin-bottom: 8px;
      }
    }
  }
}

@media (max-width: 768px) {
  .control-group {
    margin-right: 0 !important;
    width: 100%;
  }
  
  .breadcrumb {
    flex-wrap: wrap;
  }
}

// 셀렉트 박스 관련 스타일
.page-size-select {
  width: 120px;
  
  :deep(.ant-select-selector) {
    height: 32px !important;
    
    .ant-select-selection-item {
      line-height: 30px;
    }
  }
}

.container-select {
  width: 150px;
}

// 액션 버튼 관련 스타일
.action-buttons {
  display: flex;
  gap: 8px;
  justify-content: center;
}

// 페이지네이션 관련 스타일
.pagination-wrapper {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}

// 텍스트 관련 스타일
.ellipsis-text {
  display: inline-block;
  max-width: 300px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

:deep(.ant-table-cell) {
  .ant-typography {
    margin-bottom: 0;
  }
}

// 그리드 뷰 관련 스타일
.grid-view {
  .item-card {
    cursor: pointer;
    transition: all 0.3s;
    
    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
    
    &.is-folder {
      background-color: #f0f7ff;
    }
  }
  
  .item-icon {
    font-size: 2rem;
    text-align: center;
    margin-bottom: 1rem;
    
    i {
      color: #1890ff;
    }
  }
  
  .item-info {
    text-align: center;
    
    .item-name {
      font-weight: 500;
      margin-bottom: 0.5rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    
    .item-meta {
      font-size: 0.85rem;
      color: rgba(0, 0, 0, 0.45);
    }
  }
  
  .item-actions {
    display: flex;
    justify-content: center;
    gap: 8px;
    margin-top: 1rem;
    opacity: 0;
    transition: opacity 0.3s;
  }
  
  .item-card:hover .item-actions {
    opacity: 1;
  }
}

// 폴더 링크 스타일
.folder-link {
  color: #1890ff;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
}

// 네비게이션 관련 스타일
.breadcrumb {
  margin: 0;
  padding: 0;
  background: none;
  
  .breadcrumb-item {
    font-size: 0.9rem;
    
    &.active {
      color: rgba(0, 0, 0, 0.85);
    }
    
    a {
      color: #1890ff;
      text-decoration: none;
      
      &:hover {
        text-decoration: underline;
      }
    }
  }
}

// 검색 관련 스타일 추가
.search-info {
  margin-top: -8px;
  margin-bottom: 16px;
  
  :deep(.ant-alert-message) {
    width: 100%;
  }
}

// 검색 입력 필드 스타일 조정
:deep(.ant-input.ant-input-sm) {
  height: 32px !important;
  line-height: 30px;
  padding: 0 11px;
  border-radius: 4px;
}
</style>
